import React from 'react';
import styled, {css} from 'styled-components';

import {WithSidebarContext} from '../../SidebarContext';
import {useMainContentHeight} from '../../page-structure/useMainContentHeight';
import {FooterWrapper} from './Footer';
import {MainWrapper} from './Main';
import {PageGrid} from './PageContainer';
import {FilterAside} from './advanced-filter/FilterFormSetup';
import {AsideWrapper} from './aside/Aside';
import {AsideSlideInWrapper} from './aside/AsideSlideIn';
import {NoneEntityDetail} from './page-features/PageContainerTypes';
import {SizeVariable} from './page-features/PageSizeSetup';

/**
 * <Page> is a styled component for creating complex grid layouts.
 * It enables flexible grid configurations without requiring props to set up your page grid layout.
 * With it, you can create layouts where the position of other components (e.g., a sidebar) is determined by the order you write your code. Here's how to use it
 *
 * @example
 * ```jsx
 * // This code snippet facilitates the creation of a simple page layout, complete with a custom scrollbar
 *  <Page>
 *      <Main />
 *  </Page>
 *
 *  * // For fixed left sidebar
 *  <Page>
 *      <Aside />
 *      <Main />
 *  </Page>
 *
 * // For fixed right sidebar
 *  <Page>
 *      <Main />
 *      <Aside />
 *  </Page>
 *
 * // For fixed left and right sidebar
 *  <Page>
 *      <Aside />
 *      <Main />
 *      <Aside />
 *  </Page>
 *
 *  // For fixed left sidebar & fixed footer
 *  <Page>
 *      <Aside />
 *      <Main />
 *      <Footer />
 *  </Page>
 *
 *  // For fixed right sidebar & fixed footer
 *  <Page>
 *      <Main />
 *      <Footer />
 *      <Aside />
 *  </Page>
 * ```
 */
const Page = ({children}) => {
    const {targetRef} = useMainContentHeight();
    return (
        <WithSidebarContext>
            {({sidebarOpen, sidebarClosing}) => (
                <PageWrapper sidebarOpen={sidebarOpen} sidebarClosing={sidebarClosing} ref={targetRef}>
                    {children}
                </PageWrapper>
            )}
        </WithSidebarContext>
    );
};

export const AsideAnimationSizeSetup = css`
    &:has(${AsideWrapper}.sm) {
        --aside-width: var(--aside-width-small);
    }

    &:has(${AsideWrapper}.lg) {
        --aside-width: var(--aside-width-large);
    }

    &:has(${AsideWrapper}.xl) {
        --aside-width: var(--aside-width-extra-large);
    }
`;

const PageWrapper = styled(PageGrid)`
    ${SizeVariable}
    ${AsideAnimationSizeSetup}
    display: grid;
    height: 100%;
    grid-area: body;

    &:has(${FilterAside}),
    &:has(${AsideSlideInWrapper}) {
        ${({sidebarOpen, sidebarClosing}) => {
            if (sidebarOpen && !sidebarClosing) {
                return css`
                    overflow: hidden;
                    transition: 0.6s;
                    grid-template-columns: calc(100% - var(--aside-width)) var(--aside-width) !important;
                `;
            } else {
                return css`
                    overflow: hidden;
                    transition: 0.6s;
                    grid-template-columns: 100% var(--aside-width) !important;
                    ${AsideWrapper} {
                        box-shadow: none;
                    }
                `;
            }
        }}
    }

    &:has(${AsideWrapper}:nth-child(1) + ${MainWrapper}:nth-child(2)),
    &:has(${AsideWrapper}:nth-child(1) + ${MainWrapper}:nth-child(2) + ${AsideWrapper}:nth-child(3)),
    &:has(${AsideWrapper}:nth-child(1) + ${MainWrapper}:nth-child(2) + ${FooterWrapper}:nth-child(3)) {
        ${AsideWrapper}:nth-child(1) {
            box-shadow: var(--container-shadow-color-700);
        }
    }
`;

Page.Wrapper = PageWrapper;
Page.NoneEntity = NoneEntityDetail;

export {NoneEntityDetail, Page, PageWrapper};
